import { DEFAULT_THRESHOLD_BUFFER_PCT } from "@erp/shared-types";
import {
  divideMoney,
  multiplyMoney,
  roundMoney,
  subtractMoney,
  toMoneyNumber,
} from "./money";

/** Dollars still missing against one threshold, rounded to the cent; 0 once met. */
export function gapToThreshold(total: number, threshold: number): number {
  const gap = Math.round((threshold - total) * 100) / 100;
  return gap > 0 ? gap : 0;
}

/**
 * Can this PO be placed, and is it the best version of itself (GEN-2832)?
 *
 * - Firm vendor minimum order value (MOV): shown as a hard limit in the UI,
 *   but the buyer may override at release with a required comment (GEN-2957).
 *   Missing it is a warning, not "can't order".
 * - Soft "should meet": prepaid-freight threshold (constraint "2"), vendor MOQ,
 *   and buy quantities off the vendor pack multiple. Missing one is a warning.
 *
 * A firm check only blocks on the vendor's own number. The board defaults
 * below fill in where the vendor quotes none, and missing a guessed minimum
 * is a warning, not "can't order".
 */

/** Buyer review 2026-10-05: used only when the vendor quotes none. */
export const DEFAULT_PO_MINIMUM = 300;
export const DEFAULT_PREPAID_FREIGHT = 1000;

/**
 * Values at or above this are the vendor feed's "never" placeholders (e.g.
 * 99,999, 99,999.99, 888,888.80, 999,999.90 or 9,999,999), not thresholds
 * anyone can order to; they read as no constraint. The feed carries 99,999 on
 * over ten thousand prepaid-freight rows, so a cutoff at 999,999 read them as a
 * real $99,999 target and showed branch carts tens of thousands short of
 * freight.
 */
export const CONSTRAINT_PLACEHOLDER_MIN = 99_999;

export type ThresholdSource = "vendor" | "default";

export interface ThresholdCheck {
  amount: number;
  source: ThresholdSource;
  met: boolean;
  /** Dollars still missing, cent-rounded; 0 once met. */
  gap: number;
  /** Dollars above the threshold, cent-rounded; 0 while short. */
  cushion: number;
  /**
   * Met, but by less than the buffer share of it (GEN-2931), so one cancelled
   * line could drop it back under. Exactly at the threshold counts.
   */
  inBuffer: boolean;
}

export interface MoqCheck {
  met: boolean;
  /** Lines under their vendor MOQ. */
  belowCount: number;
  lineCount: number;
}

export interface IncrementCheck {
  met: boolean;
  /** Committed buy lines not on the vendor order multiple. */
  offCount: number;
  lineCount: number;
}

export const READINESS_STATES = ["ready", "warning", "not_ready"] as const;
export type ReadinessState = (typeof READINESS_STATES)[number];

export interface OrderReadiness {
  state: ReadinessState;
  total: number;
  /** Nothing committed: there is no order to place yet. */
  empty: boolean;
  mov: ThresholdCheck;
  moq: MoqCheck;
  freight: ThresholdCheck;
  increment: IncrementCheck;
  /** Ready, but the PO minimum or prepaid freight is met within its buffer. */
  withinBuffer: boolean;
  /** The buffer share the checks used, whole percent. */
  bufferPct: number;
}

function centsHalfUp(value: Parameters<typeof roundMoney>[0]): number {
  return toMoneyNumber(roundMoney(value, 2, "ROUND_HALF_UP"));
}

/** One threshold against the cart total: the vendor's amount, else the default. */
export function thresholdCheck(
  total: number,
  vendorAmount: number | null,
  fallback: number,
  bufferPct: number = DEFAULT_THRESHOLD_BUFFER_PCT,
): ThresholdCheck {
  const fromVendor = vendorAmount != null && vendorAmount > 0;
  const amount = fromVendor ? vendorAmount : fallback;
  const gap = gapToThreshold(total, amount);
  const met = gap === 0;
  // Decimal math, cent-rounded half up: binary floats round e.g. a 15%
  // buffer on $260.90 to $39.13 instead of $39.14.
  const cushion = met
    ? Math.max(0, centsHalfUp(subtractMoney(total, amount)))
    : 0;
  // No buffer on a "never" placeholder: there is no real threshold to cushion.
  const bufferAmount =
    amount >= CONSTRAINT_PLACEHOLDER_MIN
      ? 0
      : centsHalfUp(divideMoney(multiplyMoney(amount, bufferPct), 100));
  return {
    amount,
    source: fromVendor ? "vendor" : "default",
    met,
    gap,
    cushion,
    inBuffer: met && cushion < bufferAmount,
  };
}

/** True when qty is not a whole multiple of increment (increment > 0). */
export function isOffVendorIncrement(
  quantity: number,
  increment: number | null | undefined,
): boolean {
  if (increment == null || increment <= 0) return false;
  const ratio = quantity / increment;
  return Math.abs(ratio - Math.round(ratio)) > 1e-9;
}

export function orderReadiness(input: {
  total: number;
  movThreshold: number | null;
  freightThreshold: number | null;
  lineCount: number;
  belowMoqLineCount: number;
  belowOffIncrementLineCount?: number;
  /** Org threshold buffer, whole percent; one share for both thresholds. */
  bufferPct?: number;
  /**
   * The vendor's only amount is a "never" placeholder, read upstream as no
   * threshold: the default still applies, but with no buffer band.
   */
  movThresholdPlaceholder?: boolean;
  freightThresholdPlaceholder?: boolean;
}): OrderReadiness {
  const mov = thresholdCheck(
    input.total,
    input.movThreshold,
    DEFAULT_PO_MINIMUM,
    input.movThresholdPlaceholder ? 0 : input.bufferPct,
  );
  const freight = thresholdCheck(
    input.total,
    input.freightThreshold,
    DEFAULT_PREPAID_FREIGHT,
    input.freightThresholdPlaceholder ? 0 : input.bufferPct,
  );
  const moq: MoqCheck = {
    met: input.belowMoqLineCount === 0,
    belowCount: input.belowMoqLineCount,
    lineCount: input.lineCount,
  };
  const offIncrement = input.belowOffIncrementLineCount ?? 0;
  const increment: IncrementCheck = {
    met: offIncrement === 0,
    offCount: offIncrement,
    lineCount: input.lineCount,
  };
  const empty = input.total <= 0;
  const caution = !mov.met || !freight.met || !moq.met || !increment.met;
  const state = empty ? "not_ready" : caution ? "warning" : "ready";
  return {
    state,
    total: input.total,
    empty,
    mov,
    moq,
    freight,
    increment,
    withinBuffer: state === "ready" && (mov.inBuffer || freight.inBuffer),
    bufferPct: input.bufferPct ?? DEFAULT_THRESHOLD_BUFFER_PCT,
  };
}

/**
 * The per-cart facts an order's readiness is judged on, as the buying-orders
 * query aggregates them (GEN-3234): what Release would send, i.e. the cart's
 * approved lines (whole-cart dollars) and how many miss the vendor MOQ or pack
 * multiple, against the cart's minimums. Every surface showing a cart's
 * readiness (the Orders table's Order column, the readiness filter, the open
 * cart's detail header) reads these same facts, so they cannot disagree.
 */
export interface BuyingOrderReadinessFacts {
  kind: "po" | "to";
  /** Null on a row summed across ship-tos: no per-cart minimum applies. */
  shipToWarehouseId: string | null;
  approvedTotal: number;
  movThreshold: number | null;
  freightThreshold: number | null;
  movThresholdPlaceholder?: boolean;
  freightThresholdPlaceholder?: boolean;
  approvedLineCount: number;
  approvedBelowMoqLineCount: number;
  approvedBelowOffIncrementLineCount: number;
}

/**
 * A buying-order row's readiness. Null for TOs and for vendor-grouped rows,
 * which span several carts and so have no per-cart minimum.
 */
export function buyingOrderReadiness(
  row: BuyingOrderReadinessFacts,
  bufferPct?: number,
): OrderReadiness | null {
  if (row.kind !== "po" || row.shipToWarehouseId === null) return null;
  return orderReadiness({
    total: row.approvedTotal,
    movThreshold: row.movThreshold,
    freightThreshold: row.freightThreshold,
    movThresholdPlaceholder: row.movThresholdPlaceholder,
    freightThresholdPlaceholder: row.freightThresholdPlaceholder,
    lineCount: row.approvedLineCount,
    belowMoqLineCount: row.approvedBelowMoqLineCount,
    belowOffIncrementLineCount: row.approvedBelowOffIncrementLineCount,
    bufferPct,
  });
}
